import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canViewSanctioned } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSpreadsheet, Download } from "lucide-react";
import { ReportFilters } from "@/components/reports/ReportFilters";
import { SummaryTable } from "@/components/reports/SummaryTable";
import { MonthlyTable } from "@/components/reports/MonthlyTable";
import { MonthlyChart } from "@/components/reports/MonthlyChart";
import { TxnDialog } from "@/components/reports/TxnDialog";
import { currentFy, fyMonths } from "@/components/reports/format";

const qs = (o) => Object.entries(o).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");

export default function BudgetReports() {
  const { user } = useAuth();
  const showSanc = canViewSanctioned(user?.role);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState("summary");
  const [f, setF] = useState({ pid: "", fy: currentFy(), mode: "all", from: "", to: "", status: "approved", hideZero: true });
  const set = (patch) => setF(prev => ({ ...prev, ...patch }));
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [showCum, setShowCum] = useState(true);
  const [txns, setTxns] = useState(null);

  useEffect(() => {
    api.get("/projects").then(r => {
      const list = r.data.filter(p => p.project_code).sort((a, b) => a.project_code.localeCompare(b.project_code));
      setProjects(list);
      if (list.length) set({ pid: list[0].id });
    });
  }, []);

  const range = f.mode === "single" ? { month_from: f.from, month_to: f.from } : f.mode === "range" ? { month_from: f.from, month_to: f.to } : {};
  const base = { project_id: f.pid, fy: f.fy, status: f.status, hide_zero: f.hideZero };
  const ready = f.pid && (view === "monthly" || f.mode === "all" || (f.from && (f.mode === "single" || f.to)));

  useEffect(() => {
    if (!f.pid || !f.fy || (view === "summary" && f.pid === "ALL")) { setMonthly(null); return; }
    api.get(`/v2/reports/monthly?${qs(base)}`).then(r => setMonthly(r.data)).catch(() => setMonthly(null));
  }, [f.pid, f.fy, f.status, f.hideZero, view]);

  useEffect(() => {
    if (view !== "summary" || !ready || f.pid === "ALL") return;
    api.get(`/v2/reports/budget-rollup?${qs({ ...base, ...range })}`).then(r => setSummary(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Could not load report"));
  }, [f, view]);

  const switchView = (v) => {
    if (v === "monthly" && !f.fy) set({ fy: currentFy() });
    if (v === "summary" && f.pid === "ALL" && projects.length) set({ pid: projects[0].id });
    setView(v);
  };

  const exportXlsx = async () => {
    try {
      const q = qs({ ...base, ...(view === "summary" ? range : {}), view });
      const r = await api.get(`/v2/reports/budget-rollup.xlsx?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const code = f.pid === "ALL" ? "ALL" : (projects.find(p => p.id === f.pid)?.project_code || "budget");
      a.download = `${code}_${view}_${(f.fy || "ALL").replace(" ", "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const openCell = async (filter, title) => {
    const useRange = view === "summary" && !filter.month && !filter.cumulative;
    const { cumulative, ...rest } = filter;
    const q = qs({ project_id: f.pid, fy: f.fy, status: f.status, ...(useRange ? range : {}), ...(cumulative ? { month_from: "0000-01", month_to: range.month_to || fyMonths(f.fy).slice(-1)[0] || "9999-12" } : {}), ...rest });
    const r = await api.get(`/v2/reports/cell-txns?${q}`);
    setTxns({ ...r.data, title: `${title} · ${filter.month || (r.data.from === r.data.to ? r.data.from : `${r.data.from} to ${r.data.to}`)}` });
  };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="budget-reports-page">
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-6 h-6" /> Budget Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Vendor bills + Reimbursement claims, by Expense Month. Amount = invoice total.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5 bg-slate-50" data-testid="report-view-toggle">
            {["summary", "monthly"].map(v => (
              <button key={v} onClick={() => switchView(v)} data-testid={`report-view-${v}`}
                className={`px-3 py-1 text-sm rounded transition-colors ${view === v ? "bg-white shadow font-semibold" : "text-slate-500"}`}>
                {v === "summary" ? "Summary" : "Monthly"}
              </button>
            ))}
          </div>
          <Button onClick={exportXlsx} disabled={!ready} data-testid="reports-export-btn"><Download className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>
      <ReportFilters projects={projects} f={f} set={set} view={view} />
      {f.fy && <MonthlyChart data={monthly} showCum={showCum} setShowCum={setShowCum} />}
      {view === "summary" && summary && <SummaryTable data={summary} showSanc={showSanc} onOpenSub={(s) => openCell({ subtype_id: s.id, cumulative: !s.spent_period && s.spent_cum > 0 }, s.code)} />}
      {view === "monthly" && monthly && <MonthlyTable data={monthly} onCell={openCell} onPickProject={(id) => set({ pid: id })} />}
      <TxnDialog data={txns} onClose={() => setTxns(null)} />
    </div>
  );
}
